import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader, PageBody } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Check, Sparkle, Users, Buildings, MinusCircle, PlusCircle, CreditCard } from "@phosphor-icons/react";

const ORDER = ["starter", "team", "group", "group_plus"];

export default function SubscriptionPage() {
  const { formatApiError, loadCompanies } = useAuth();
  const location = useLocation();
  const [plans, setPlans] = useState({});
  const [seatAddon, setSeatAddon] = useState(null);
  const [me, setMe] = useState(null);
  const [interval, setInterval] = useState("month");
  const [seatCount, setSeatCount] = useState(0);
  const [loading, setLoading] = useState(null);
  const [seatSaving, setSeatSaving] = useState(false);

  const load = async () => {
    const [p, m] = await Promise.all([
      api.get("/subscription/plans"),
      api.get("/subscription/me"),
    ]);
    setPlans(p.data.plans || {});
    setSeatAddon(p.data.seat_addon || null);
    setMe(m.data);
    setSeatCount(m.data.seat_addons || 0);
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    if (qs.get("checkout") === "success" && qs.get("session_id")) {
      (async () => {
        try {
          const { data } = await api.get(`/subscription/verify/${qs.get("session_id")}`);
          if (data.paid) {
            toast.success(`Subscription activated: ${plans[data.plan]?.name || data.plan} (${data.interval}ly)`);
            await load();
            await loadCompanies();
          } else {
            toast.error("Payment not confirmed yet — refresh in a moment.");
          }
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
        window.history.replaceState({}, "", "/subscription");
      })();
    } else if (qs.get("checkout") === "cancel") {
      toast.info("Checkout cancelled");
      window.history.replaceState({}, "", "/subscription");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const subscribe = async (planId) => {
    setLoading(planId);
    try {
      const { data } = await api.post("/subscription/checkout", {
        plan: planId,
        interval,
        seat_addons: seatCount,
        success_url: `${window.location.origin}/subscription?checkout=success&session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/subscription?checkout=cancel`,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setLoading(null);
    }
  };

  const updateSeats = async () => {
    setSeatSaving(true);
    try {
      const { data } = await api.post("/subscription/seats", { seat_addons: seatCount });
      toast.success(
        data.stripe_updated
          ? `Seats updated to ${data.seat_addons} · billed on next invoice`
          : `Seat plan set to ${data.seat_addons} — will apply at checkout`
      );
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSeatSaving(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel your subscription? Access continues until the end of the current period.")) return;
    try {
      await api.post("/subscription/cancel");
      toast.success("Subscription cancelled");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const openPortal = async () => {
    try {
      const { data } = await api.post("/subscription/portal");
      window.location.href = data.portal_url;
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      if (typeof detail === "string" && /configuration|test mode|not been created/i.test(detail)) {
        toast.error("Stripe Customer Portal isn't configured yet. Enable it at https://dashboard.stripe.com/test/settings/billing/portal");
      } else {
        toast.error(formatApiError(detail) || e.message);
      }
    }
  };

  const currentPlanId = me?.plan;
  const isGrandfathered = me?.plan_status === "grandfathered";
  const seatPriceLabel = seatAddon?.prices?.[interval]?.label;

  return (
    <>
      <PageHeader
        eyebrow="Billing"
        title="Subscription"
        description={
          isGrandfathered
            ? "You're on a legacy plan (grandfathered as an early user). No charge — includes group consolidation."
            : "Pick the plan that fits your business. Add seats as your team grows. Cancel anytime."
        }
      />
      <PageBody>
        {me && (
          <div className="border border-border bg-card p-4 grid gap-4 md:grid-cols-3" data-testid="sub-current">
            <div>
              <div className="overline text-muted-foreground">Current Plan</div>
              <div className="font-serif-display text-2xl mt-1">
                {plans[me.plan]?.name || me.plan}
                <span className={`ml-3 tag-badge ${me.plan_status === "active" || me.plan_status === "grandfathered" ? "text-primary" : "text-muted-foreground"}`}>
                  {me.plan_status}
                </span>
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                {me.plan_interval && `Billed ${me.plan_interval}ly`}
              </div>
            </div>
            <div>
              <div className="overline text-muted-foreground flex items-center gap-1"><Buildings size={12} /> Companies</div>
              <div className="font-serif-display text-2xl mt-1 num" data-testid="sub-companies-used">
                {me.companies_used} <span className="text-muted-foreground text-lg">/ {me.companies_limit}</span>
              </div>
            </div>
            <div>
              <div className="overline text-muted-foreground flex items-center gap-1"><Users size={12} /> User seats</div>
              <div className="font-serif-display text-2xl mt-1 num" data-testid="sub-seats-used">
                {me.seats_used} <span className="text-muted-foreground text-lg">/ {me.seats_limit}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {me.base_user_limit} base + {me.seat_addons} add-on
              </div>
            </div>
            {me.plan_status === "active" && (
              <div className="md:col-span-3 flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={openPortal}
                  data-testid="sub-portal-btn"
                  className="rounded-md font-mono text-xs uppercase tracking-wider gap-2"
                >
                  <CreditCard size={14} weight="fill" /> Manage Billing
                </Button>
                <Button variant="outline" onClick={cancel} data-testid="sub-cancel-btn" className="rounded-md font-mono text-xs uppercase tracking-wider">
                  Cancel Subscription
                </Button>
              </div>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-4">
          <Tabs value={interval} onValueChange={setInterval}>
            <TabsList className="rounded-sm">
              <TabsTrigger value="month" data-testid="int-month" className="font-mono uppercase text-xs tracking-wider">Monthly</TabsTrigger>
              <TabsTrigger value="year" data-testid="int-year" className="font-mono uppercase text-xs tracking-wider">Annual · save 2 months</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="flex items-center gap-3 border border-border bg-card px-3 py-2 rounded-sm">
            <div>
              <Label className="overline text-muted-foreground">Extra login seats</Label>
              <div className="text-xs text-muted-foreground">{seatPriceLabel || "£25 / user / month"}</div>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setSeatCount(Math.max(0, seatCount - 1))}
                className="p-1 hover:text-primary"
                data-testid="seat-decr"
                aria-label="Decrease seats"
              >
                <MinusCircle size={22} weight="regular" />
              </button>
              <Input
                type="number"
                min={0}
                max={50}
                value={seatCount}
                onChange={(e) => setSeatCount(Math.max(0, Math.min(50, parseInt(e.target.value || "0", 10))))}
                className="w-16 text-center rounded-sm h-9 num"
                data-testid="seat-input"
              />
              <button
                type="button"
                onClick={() => setSeatCount(Math.min(50, seatCount + 1))}
                className="p-1 hover:text-primary"
                data-testid="seat-incr"
                aria-label="Increase seats"
              >
                <PlusCircle size={22} weight="regular" />
              </button>
            </div>
            {me?.plan_status === "active" && me?.seat_addons !== seatCount && (
              <Button size="sm" onClick={updateSeats} disabled={seatSaving} data-testid="seat-save" className="rounded-sm h-9 font-mono uppercase text-xs tracking-wider">
                {seatSaving ? "Saving…" : "Update seats"}
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {ORDER.filter((id) => plans[id]).map((id) => {
            const plan = plans[id];
            const price = plan.prices?.[interval];
            const isCurrent = currentPlanId === id && (me?.plan_status === "active" || me?.plan_status === "grandfathered");
            const isFeatured = id === "group";
            return (
              <div
                key={id}
                className={`border ${isCurrent ? "border-primary bg-primary/5" : isFeatured ? "border-primary/40 bg-card" : "border-border bg-card"} p-5 flex flex-col`}
                data-testid={`plan-card-${id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="overline">{isFeatured ? "Most Popular" : "\u00A0"}</div>
                    <div className="font-serif-display text-2xl">{plan.name}</div>
                    <div className="text-xs text-muted-foreground mt-1">{plan.tagline}</div>
                  </div>
                  {isFeatured && <Sparkle size={20} className="text-primary" weight="fill" />}
                </div>
                <div className="font-serif-display text-3xl num mt-3">{price?.label}</div>
                {price?.note && <div className="text-xs text-muted-foreground -mt-1">{price.note}</div>}

                <div className="mt-3 flex items-center gap-3 text-xs">
                  <span className="flex items-center gap-1"><Buildings size={14} /> {plan.company_limit} {plan.company_limit === 1 ? "company" : "companies"}</span>
                  <span className="flex items-center gap-1"><Users size={14} /> {plan.user_limit} users</span>
                </div>

                <ul className="space-y-2 text-sm mt-4 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check size={16} className="text-primary shrink-0 mt-0.5" weight="bold" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                {seatCount > 0 && (
                  <div className="text-xs text-muted-foreground mt-3 border-t border-border pt-2">
                    + {seatCount} extra seat{seatCount > 1 ? "s" : ""} ({seatAddon?.prices?.[interval]?.label})
                  </div>
                )}
                <Button
                  onClick={() => subscribe(id)}
                  disabled={loading === id || isCurrent}
                  data-testid={`sub-btn-${id}`}
                  className="w-full rounded-sm h-11 font-mono uppercase tracking-wider text-xs mt-4"
                >
                  {isCurrent ? "Current Plan" : loading === id ? "Loading…" : `Choose ${plan.name}`}
                </Button>
              </div>
            );
          })}
        </div>
      </PageBody>
    </>
  );
}
